<!-- Slider.svelte -->
<script>
  /**
   * @typedef {Object} Props
   * @property {number} [min]
   * @property {number} [max]
   * @property {number} [value]
   * @property {import('svelte').Snippet} [children]
   */

  /** @type {Props} */
  let {
    min = 0,
    max = 100,
    value = $bindable(), // ✅ Remove fallback from $bindable, da problemas en Svelte 5
    children,
  } = $props();
  value ??= 50; // ✅ Set default after destructuring

  function interpolate(x, x1, x2, y1, y2) {
    return ((x - x1) * (y2 - y1)) / (x2 - x1) + y1;
  }

  // let hue = (value - min) / (max - min) * 240; // Interpolating between red (0) and blue (240).
  let hue = $derived(
    value < (max - min) / 2
      ? interpolate(value, min, (max - min) / 2, 20, 50) // From dark red to #00b4d8
      : interpolate(value, (max - min) / 2, max, 197, 180),
  ); // From #00b4d8 to teal

  // --- Mobile-friendly tooltip state & logic ---
  let isDragging = $state(false);
  let thumbPosition = $state(0); // percentage (0-100)
  /** @type {HTMLInputElement | undefined} */
  let sliderRef;

  function updateThumbPosition() {
    // Calculate percentage position of the thumb within [min, max]
    const range = max - min;
    const pct = range === 0 ? 0 : ((value - min) / range) * 100;
    thumbPosition = Math.min(100, Math.max(0, pct));
  }

  function handleStart() {
    isDragging = true;
    updateThumbPosition();
  }

  function handleEnd() {
    isDragging = false;
  }

  function handleInput() {
    updateThumbPosition();
  }

  // Keep tooltip position in sync when value/min/max changes
  $effect(() => {
    // reference the values to make them dependencies
    void value; void min; void max; void sliderRef;
    updateThumbPosition();
  });
</script>

<div class="Slider" style="--thumb-hue: {hue}">
  <div class="left-content">
    {@render children?.()}
  </div>
  <label>
    <!-- Drag-time tooltip (positioned relative to label so it follows the track) -->
    <div
      class="value-tooltip"
      class:visible={isDragging}
      style="left: {thumbPosition}%"
      aria-hidden={!isDragging}
    >
      {value}
    </div>
    <input
      type="range"
      bind:this={sliderRef}
      {min}
      {max}
      step="5"
      bind:value
      aria-label="Value slider"
      oninput={handleInput}
      ontouchstart={handleStart}
      ontouchend={handleEnd}
      onpointerdown={handleStart}
      onpointerup={handleEnd}
    />
  </label>
  <div class="static-value">{value}</div>
</div>

<style>
  .Slider {
    display: grid;
    grid-template-columns: minmax(120px, 1fr) 2fr auto; /* Fixed minimum width for consistent layout */
    align-items: center;
    gap: 8px;
    padding: 0.5em;
    padding-inline: 1em;
    min-width: 0; /* Allow grid items to shrink below their content size */
    box-sizing: border-box;
    width: 100%; /* Ensure full width usage */
  }

  .left-content {
    min-width: 120px; /* Consistent minimum width */
    max-width: 200px; /* Prevent excessive expansion */
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 3; /* Standard property for compatibility */
    -webkit-line-clamp: 3; /* Limit to 3 lines */
    -webkit-box-orient: vertical;
    line-height: 1.2em;
    max-height: 3.6em; /* 3 lines * 1.2em line height */
    font-size: 0.8em;
    padding-right: 8px;
    word-break: break-word;
    box-sizing: border-box;
  }

  /* Position tooltip relative to the range label */
  label {
    position: relative;
  }

  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    min-width: 0; /* Allow the slider to shrink */
    height: 8px;
    background: #ddd;
    outline: none;
    padding: 0;
    margin: 8px 0;
    transition: background-color 0.2s;
    border-radius: 5px;
  }

  input[type=range]:active,  /* Changing track color slightly when slider is active */
input[type=range]:hover {
    background: #ccc;
  }

  input[type="range"]::-moz-range-track {
    background: #ddd;
    height: 2px;
    border-radius: 3px;
    outline: none;
  }

  input[type="range"]::-moz-range-thumb {
    appearance: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    /* background: #2196F3; */
    cursor: pointer;
    border: none;
    outline: none;
    transition:
      background-color 0.2s,
      transform 0.2s; /* Smooth transition for hover effect */
    background: hsl(var(--thumb-hue), 100%, 50%); /* gradual change in color */
  }

  input[type=range]::-moz-range-thumb:hover,  /* Slight enlargement and color change for hover */
input[type=range]::-moz-range-thumb:active {
    /* background: #1875c7; */
    /* background: hsl(var(--thumb-hue), 100%, 40%); */
    transform: scale(1.2);
  }

  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    /* background: #9CFFFA; */
    /* background: #2196F3; */
    cursor: pointer;
    margin-top: -13px;
    border: none;
    outline: none;
    transition:
      background-color 0.2s,
      transform 0.2s; /* Smooth transition for hover effect */
    background: hsl(var(--thumb-hue), 100%, 50%); /* gradual change in color */
  }

  input[type=range]::-webkit-slider-thumb:hover,  /* Slight enlargement and color change for hover */
input[type=range]::-webkit-slider-thumb:active {
    /* background: #1875c7; */
    /* background: hsl(var(--thumb-hue), 100%, 40%); */
    transform: scale(1.2);
  }

  /* Drag-time tooltip styling */
  .value-tooltip {
    position: absolute;
    top: -38px;
    transform: translateX(-50%);
    background: #333;
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.8em;
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
    pointer-events: none;
    z-index: 10;
    white-space: nowrap;
  }

  .value-tooltip.visible {
    opacity: 1;
  }

  .value-tooltip::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px 5px 0 5px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
  }

  /* Static value (desktop) */
  .static-value {
    align-self: center;
  }

  /* Responsive behavior: on small screens prefer tooltip while dragging */
  @media (max-width: 767px) {
    .static-value {
      display: none;
    }
    
    .Slider {
      grid-template-columns: minmax(100px, 1fr) 2fr; /* Remove static value column on mobile */
    }
    
    .left-content {
      min-width: 100px;
      max-width: 150px;
      font-size: 0.75em;
    }
  }
</style>
